import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  getFinanceRequest,
  getFinanceRequests,
  getMyFinanceManager,
  saveFinanceRequest,
  submitFinanceRequest
} from '../../../shared/services/data/finance-request.data';
import {
  FinanceRequestAdvanceModel,
  FinanceRequestEmployeeModel,
  FinanceRequestLineModel,
  FinanceRequestManagerModel,
  FinanceRequestType,
  SaveFinanceRequestPayload
} from '../../../shared/models/finance-request.model';
import { FinanceRequestUiModel } from '../../../shared/ui-models/finance-request.ui-model';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { LoadingData } from '../../../ui/LoadingData';
import {
  addDays,
  BTN_OUTLINE,
  BTN_PRIMARY,
  BTN_SECONDARY,
  CATEGORIES,
  CONTENT,
  CURRENCIES,
  DECLARATION,
  DEFAULT_SETTLE_DAYS,
  detailsPath,
  FIELDS,
  fmtDate,
  INPUT,
  LABEL,
  LINK,
  listPath,
  liveTotals,
  money,
  NOTE_ATTENTION,
  num,
  PAGE,
  PAGE_TITLE,
  PAY_METHODS,
  READONLY,
  REIMBURSE_METHODS,
  REMOVE,
  RETURN_METHODS,
  SECTION,
  SECTION_TITLE,
  TABLE_WRAP,
  TD,
  TD_INPUT,
  TFOOT,
  TH,
  today,
  TYPES
} from '../../shared/finance-request.util';
import { RequestSelect, toOptions } from '../../shared/request-select.component';
import Tooltip from '../../../ui/Tooltip';

type Line = Omit<FinanceRequestLineModel, 'amount'> & { amount: string };
type Advance = Omit<FinanceRequestAdvanceModel, 'amount'> & { amount: string };
interface Draft {
  employee: Partial<FinanceRequestEmployeeModel>;
  form: Record<string, string>;
  advances: Advance[];
  lines: Line[];
  acknowledged: boolean;
}

const FORM_KEYS = [
  'purpose',
  'project',
  'costCenter',
  'from',
  'to',
  'currency',
  'settleDays',
  'needBy',
  'payMethod',
  'iban',
  'requested',
  'returnDate',
  'returnMethod',
  'returnVoucher',
  'reimburseMethod'
];
const NUMERIC_KEYS = ['settleDays', 'requested'];
const CATEGORY_OPTIONS = toOptions(CATEGORIES);
const blank = (): Line => ({ amount: '', receipt: false });

function fromRequest(r: FinanceRequestUiModel): Draft {
  const form: Record<string, string> = {};
  FORM_KEYS.forEach((k) => {
    const v = (r.form as unknown as Record<string, unknown>)[k];
    form[k] = v === undefined || v === null ? '' : String(v);
  });
  return {
    form,
    employee: r.employee,
    advances: r.advances.map((a) => ({ ...a, amount: String(a.amount ?? '') })),
    lines: r.lines.length
      ? r.lines.map((l) => ({ ...l, amount: l.amount ? String(l.amount) : '' }))
      : [blank()],
    acknowledged: r.acknowledged
  };
}

function toPayload(type: FinanceRequestType, d: Draft): SaveFinanceRequestPayload {
  const form: Record<string, string | number> = {};
  Object.entries(d.form).forEach(([k, v]) => {
    if (v === '') return;
    form[k] = NUMERIC_KEYS.includes(k) ? num(v) : v;
  });
  return {
    type,
    employee: {
      name: d.employee.name,
      employeeId: d.employee.employeeId,
      department: d.employee.department,
      position: d.employee.position,
      contact: d.employee.contact
    },
    form: { currency: 'EGP', ...form } as SaveFinanceRequestPayload['form'],
    advances: type === 'STL' ? d.advances.map((a) => ({ ...a, amount: num(a.amount) })) : [],
    lines: d.lines
      .filter((l) => l.amount !== '' || l.category || l.description || l.date)
      .map(({ verified, ...l }) => ({ ...l, amount: num(l.amount) })),
    acknowledged: d.acknowledged
  };
}

function missingFields(type: FinanceRequestType, d: Draft, advanced: number): string[] {
  const out: string[] = [];
  const f = d.form;
  const lines = d.lines.filter((l) => num(l.amount) > 0);
  if (!(d.employee.name || '').trim()) out.push('Employee name');
  if (!(f.purpose || '').trim()) out.push('Purpose');
  if (type === 'ADV') {
    if (!f.from || !f.to) out.push('Period from and to');
    if (!lines.length) out.push('At least one estimated expense');
    if (lines.some((l) => !l.category)) out.push('A category on every expense');
    if (!(num(f.requested) > 0)) out.push('Amount requested');
    if (f.payMethod === 'Bank transfer' && !(f.iban || '').trim()) out.push('Bank account / IBAN');
  } else {
    if (!d.advances.length || !(advanced > 0)) out.push('The advance you are settling');
    if (!lines.length) out.push('At least one expense line');
    if (lines.some((l) => !l.date || !l.category)) out.push('A date and category on every line');
  }
  if (f.from && f.to && f.from > f.to) out.push('A period that ends after it starts');
  if (!d.acknowledged) out.push(type === 'ADV' ? 'The undertaking' : 'The declaration');
  return out;
}

const Field: React.FC<{ label: string; required?: boolean; wide?: boolean }> = ({
  label,
  required,
  wide,
  children
}) => (
  <div className={`${LABEL} ${wide ? 'col-span-full' : ''}`}>
    <span>
      {label}
      {required ? <span className="text-scarlet"> *</span> : null}
    </span>
    {children}
  </div>
);

export const RequestFormContainer: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams<{ type?: string; requestId?: string }>();
  const fromAdvance = new URLSearchParams(location.search).get('advance');

  const [existing, setExisting] = useState<FinanceRequestUiModel | undefined>();
  const [type, setType] = useState<FinanceRequestType>(params.type === 'STL' ? 'STL' : 'ADV');
  const [draft, setDraft] = useState<Draft | undefined>();
  const [manager, setManager] = useState<FinanceRequestManagerModel | null | undefined>();
  const [approvedAdvances, setApprovedAdvances] = useState<FinanceRequestUiModel[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([
      params.requestId ? getFinanceRequest(params.requestId) : Promise.resolve(undefined),
      getFinanceRequests({ view: 'mine' }),
      getMyFinanceManager()
    ]).then(([current, mine, mgr]) => {
      if (!live) return;
      const own = isErrorInfo(mine) ? [] : mine;
      if (isErrorInfo(mgr)) setErrors([mgr.message || mgr.errorMessage]);
      setManager(isErrorInfo(mgr) ? null : mgr);
      setApprovedAdvances(own.filter((r) => r.type === 'ADV' && r.status === 'approved'));
      if (current && !isErrorInfo(current)) {
        setExisting(current);
        setType(current.type);
        setDraft(fromRequest(current));
        return;
      }
      const t: FinanceRequestType = params.type === 'STL' ? 'STL' : 'ADV';
      const last = own.find((r) => r.employee && r.employee.name);
      const next: Draft = {
        employee: last ? last.employee : {},
        form: {
          currency: 'EGP',
          settleDays: String(DEFAULT_SETTLE_DAYS),
          payMethod: t === 'ADV' ? 'Cash' : ''
        },
        advances: [],
        lines: [blank(), blank(), blank()],
        acknowledged: false
      };
      const adv = fromAdvance ? own.find((r) => r.id === fromAdvance) : undefined;
      if (t === 'STL' && adv) {
        next.advances = [
          {
            requestId: adv.id,
            serial: adv.serial || '',
            date: adv.submittedAt ? adv.submittedAt.toISOString().slice(0, 10) : '',
            amount: String(adv.totals.requested || 0)
          }
        ];
        next.form = {
          ...next.form,
          currency: adv.form.currency,
          purpose: adv.form.purpose || '',
          project: adv.form.project || '',
          costCenter: adv.form.costCenter || '',
          from: adv.form.from || '',
          to: adv.form.to || '',
          settleDays: String(adv.form.settleDays ?? DEFAULT_SETTLE_DAYS)
        };
      }
      setDraft(next);
    });
    return () => {
      live = false;
    };
  }, [params.requestId, params.type, fromAdvance]);

  const advanced = useMemo(
    () => (draft ? draft.advances.reduce((a, x) => a + num(x.amount), 0) : 0),
    [draft]
  );

  if (!draft || manager === undefined) return <LoadingData />;

  const f = draft.form;
  const cur = f.currency || 'EGP';
  const prevOutstanding = approvedAdvances
    .filter((a) => a.advanceState !== 'settled' && a.form.currency === cur)
    .reduce((sum, a) => sum + (a.totals.requested || 0), 0);
  const lines: FinanceRequestLineModel[] = draft.lines.map((l) => ({
    ...l,
    amount: num(l.amount)
  }));
  const totals = liveTotals(
    type,
    { ...f, currency: cur, requested: num(f.requested), prevOutstanding },
    lines,
    advanced
  );
  const due = addDays(f.to, f.settleDays === '' ? undefined : num(f.settleDays));
  const returned =
    existing && existing.status === 'returned'
      ? [...existing.history].reverse().find((h) => h.action === 'returned')
      : undefined;
  const usable = approvedAdvances.filter(
    (a) => a.advanceState === 'open' && !draft.advances.some((x) => x.requestId === a.id)
  );
  const serial = existing && existing.serial;
  const missing = missingFields(type, draft, advanced);
  const lockReason = !manager
    ? 'Your manager is not set in Teams'
    : missing.length
    ? `Still needed: ${missing.join(', ')}`
    : '';
  const submitButton = (
    <button
      type="button"
      className={BTN_PRIMARY}
      disabled={busy || !!lockReason}
      onClick={() => save(true)}
    >
      {busy
        ? 'Submitting…'
        : existing && existing.status === 'returned'
        ? 'Resubmit for approval'
        : 'Submit for approval'}
    </button>
  );

  const set = (fn: (d: Draft) => void) =>
    setDraft((prev) => {
      if (!prev) return prev;
      const next: Draft = JSON.parse(JSON.stringify(prev));
      fn(next);
      return next;
    });
  const setF = (k: string, v: string) => set((d) => (d.form[k] = v));
  const setEmp = (k: keyof FinanceRequestEmployeeModel, v: string) =>
    set((d) => ((d.employee as Record<string, string>)[k] = v));
  const setLine = (i: number, k: keyof Line, v: string | boolean) =>
    set((d) => ((d.lines[i] as Record<string, unknown>)[k] = v));
  const addAdvance = (id: string) => {
    const adv = usable.find((a) => a.id === id);
    if (!adv) return;
    set((d) => {
      d.advances.push({
        requestId: adv.id,
        serial: adv.serial || '',
        date: adv.submittedAt ? adv.submittedAt.toISOString().slice(0, 10) : '',
        amount: String(adv.totals.requested || 0)
      });
      d.form.currency = adv.form.currency;
      ['purpose', 'project', 'costCenter', 'from', 'to'].forEach((k) => {
        if (!d.form[k])
          d.form[k] = String((adv.form as unknown as Record<string, unknown>)[k] || '');
      });
    });
  };

  const input = (k: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input
      aria-label={k}
      className={INPUT}
      value={f[k] || ''}
      onChange={(e) => setF(k, e.target.value)}
      {...props}
    />
  );
  const select = (k: string, options: string[]) => (
    <RequestSelect
      ariaLabel={k}
      value={f[k] || ''}
      options={toOptions(options)}
      onChange={(v) => setF(k, v)}
    />
  );
  const empInput = (k: keyof FinanceRequestEmployeeModel) => (
    <input
      aria-label={k}
      className={INPUT}
      value={(draft.employee[k] as string) || ''}
      onChange={(e) => setEmp(k, e.target.value)}
    />
  );

  const save = (submit: boolean) => {
    setErrors([]);
    setBusy(true);
    saveFinanceRequest(toPayload(type, draft), existing ? existing.id : undefined)
      .then((saved: FinanceRequestUiModel | ErrorInfo) => {
        if (isErrorInfo(saved)) throw saved;
        setExisting(saved);
        if (!submit) return { request: saved, ticketWarning: undefined };
        return submitFinanceRequest(saved.id).then((result) => {
          if (isErrorInfo(result)) throw result;
          return result;
        });
      })
      .then(({ request, ticketWarning }) => {
        if (submit) {
          birdToast.success(
            `${request.serial} sent to ${request.manager?.name || 'your manager'} for approval`
          );
          if (ticketWarning) birdToast.warning(ticketWarning);
        } else {
          birdToast.success('Draft saved');
        }
        history.push(detailsPath(request.id));
      })
      .catch((error: ErrorInfo) => {
        setErrors([error.message || error.errorMessage || 'Something went wrong']);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className={PAGE}>
      <div className={`${CONTENT} pt-[32px] pb-[24px]`}>
        <button
          type="button"
          className={LINK}
          onClick={() => history.push(existing ? detailsPath(existing.id) : listPath())}
        >
          ← Back
        </button>
        <div className="mt-[16px] flex flex-col justify-between gap-[16px] border-b-2 border-neutral-500 pb-[20px] md:flex-row md:items-end">
          <h1 className={`${PAGE_TITLE} uppercase`}>{TYPES[type].name} Form</h1>
          <span className="font-gilroy-medium text-[18px] text-neutral-500">
            {serial || `${type}-${new Date().getFullYear()}-····`}
          </span>
        </div>
        {returned ? (
          <div className={`${NOTE_ATTENTION} mt-[20px]`}>
            <span className="font-gilroy-medium">
              Returned by {returned.by.name || 'your approver'}:
            </span>{' '}
            {returned.comment}
          </div>
        ) : null}
        {errors.length > 0 ? (
          <div role="alert" className={`${NOTE_ATTENTION} mt-[20px]`}>
            {errors.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </div>
        ) : null}
      </div>

      <div className={`${CONTENT} pb-[24px]`}>
        <div className="divide-y divide-gray-200">
          <section className={SECTION}>
            <h2 className={SECTION_TITLE}>1. Employee details</h2>
            <div className={FIELDS}>
              <Field label="Employee name" required>
                {empInput('name')}
              </Field>
              <Field label="Employee ID">{empInput('employeeId')}</Field>
              <Field label="Department">{empInput('department')}</Field>
              <Field label="Position">{empInput('position')}</Field>
              <Field label="Manager (first approver)" required>
                {manager ? (
                  <div className={READONLY}>{manager.name || manager.userId}</div>
                ) : (
                  <div className={`${READONLY} bg-secondary-50 text-secondary-700`}>
                    Not set in Teams
                  </div>
                )}
              </Field>
              {type === 'ADV' ? <Field label="Contact / email">{empInput('contact')}</Field> : null}
            </div>
          </section>

          {type === 'ADV' ? (
            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>2. Advance request details</h2>
              <div className={FIELDS}>
                <Field label="Request reference no.">
                  <div className={READONLY}>{serial || 'Assigned on submit'}</div>
                </Field>
                <Field label="Request date">
                  <div className={READONLY}>
                    {fmtDate((existing && existing.submittedAt) || today())}
                  </div>
                </Field>
                <Field label="Purpose of trip / activity" required wide>
                  {input('purpose')}
                </Field>
                <Field label="Destination / project">{input('project')}</Field>
                <Field label="Cost center">{input('costCenter')}</Field>
                <Field label="Period covered – from" required>
                  {input('from', { type: 'date' })}
                </Field>
                <Field label="Period covered – to" required>
                  {input('to', { type: 'date' })}
                </Field>
                <Field label="Date advance needed by">{input('needBy', { type: 'date' })}</Field>
                <Field label="Currency" required>
                  {select('currency', CURRENCIES)}
                </Field>
                <Field label="Preferred payment method">{select('payMethod', PAY_METHODS)}</Field>
                <Field
                  label="Bank account / IBAN (if transfer)"
                  required={f.payMethod === 'Bank transfer'}
                >
                  {input('iban')}
                </Field>
                <Field label="Settlement deadline (days after period end)">
                  {input('settleDays', { type: 'number', min: 0 })}
                </Field>
                <Field label="Settlement due date">
                  <div className={READONLY}>{due ? fmtDate(due) : '—'}</div>
                </Field>
              </div>
            </section>
          ) : (
            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>2. Advance details</h2>
              <div className={TABLE_WRAP}>
                <table className="w-full min-w-[720px] border-collapse">
                  <thead className="bg-creams-100">
                    <tr>
                      <th className={TH}>Advance reference no.</th>
                      <th className={TH}>Advance date</th>
                      <th className={TH}>Voucher / transfer no.</th>
                      <th className={`${TH} text-right`}>Amount advanced</th>
                      <th className={TH} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {draft.advances.map((a, i) => (
                      <tr key={i} className="bg-neutral-0">
                        {a.requestId ? (
                          <>
                            <td className={`${TD} font-gilroy-medium`}>{a.serial}</td>
                            <td className={`${TD} text-neutral-300`}>{fmtDate(a.date)}</td>
                            <td className={`${TD} text-neutral-300`}>{a.voucher || '—'}</td>
                            <td className={`${TD} text-right`}>{money(num(a.amount))}</td>
                          </>
                        ) : (
                          <>
                            <td className={TD_INPUT}>
                              <input
                                aria-label="Advance reference"
                                className={INPUT}
                                value={a.serial || ''}
                                onChange={(e) =>
                                  set((d) => (d.advances[i].serial = e.target.value))
                                }
                              />
                            </td>
                            <td className={TD_INPUT}>
                              <input
                                aria-label="Advance date"
                                type="date"
                                className={INPUT}
                                value={a.date || ''}
                                onChange={(e) => set((d) => (d.advances[i].date = e.target.value))}
                              />
                            </td>
                            <td className={TD_INPUT}>
                              <input
                                aria-label="Voucher"
                                className={INPUT}
                                value={a.voucher || ''}
                                onChange={(e) =>
                                  set((d) => (d.advances[i].voucher = e.target.value))
                                }
                              />
                            </td>
                            <td className={TD_INPUT}>
                              <input
                                aria-label="Amount advanced"
                                type="number"
                                step="0.01"
                                className={`${INPUT} text-right`}
                                value={a.amount}
                                onChange={(e) =>
                                  set((d) => (d.advances[i].amount = e.target.value))
                                }
                              />
                            </td>
                          </>
                        )}
                        <td className={`${TD_INPUT} w-[48px]`}>
                          <button
                            type="button"
                            aria-label="Remove advance"
                            className={REMOVE}
                            onClick={() => set((d) => d.advances.splice(i, 1))}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!draft.advances.length ? (
                      <tr className="bg-neutral-0">
                        <td colSpan={5} className={`${TD} text-neutral-300`}>
                          No advance added yet
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                  <tfoot className={TFOOT}>
                    <tr>
                      <td colSpan={3} className="px-[16px] py-[12px]">
                        Amount advanced
                      </td>
                      <td className="px-[16px] py-[12px] text-right">{money(advanced, cur)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="mt-[16px] flex flex-wrap items-center gap-[12px]">
                {usable.length > 0 ? (
                  <div className="min-w-[280px] flex-1">
                    <RequestSelect
                      ariaLabel="Add an approved advance"
                      value=""
                      placeholder="Add one of my approved advances"
                      options={usable.map((a) => ({
                        value: a.id,
                        label: `${a.serial} · ${money(a.totals.requested, a.form.currency)} · ${
                          a.form.purpose || ''
                        }`
                      }))}
                      onChange={addAdvance}
                    />
                  </div>
                ) : null}
                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={() =>
                    set((d) => d.advances.push({ serial: '', date: '', amount: '', voucher: '' }))
                  }
                >
                  + Advance not in this system
                </button>
              </div>
              <div className={`${FIELDS} mt-[24px]`}>
                <Field label="Currency" required>
                  {select('currency', CURRENCIES)}
                </Field>
                <Field label="Purpose of advance" required wide>
                  {input('purpose')}
                </Field>
                <Field label="Destination / project">{input('project')}</Field>
                <Field label="Cost center">{input('costCenter')}</Field>
                <Field label="Period covered – from">{input('from', { type: 'date' })}</Field>
                <Field label="Period covered – to">{input('to', { type: 'date' })}</Field>
                <Field label="Settlement deadline (days after period end)">
                  {input('settleDays', { type: 'number', min: 0 })}
                </Field>
                <Field label="Settlement due date">
                  <div className={READONLY}>{due ? fmtDate(due) : '—'}</div>
                </Field>
              </div>
            </section>
          )}

          <section className={SECTION}>
            <h2 className={SECTION_TITLE}>
              3. {type === 'ADV' ? 'Estimated expenses' : 'Expense breakdown'}
            </h2>
            <div className={TABLE_WRAP}>
              <table className="w-full min-w-[960px] border-collapse">
                <thead className="bg-creams-100">
                  <tr>
                    {type === 'STL' ? <th className={TH}>Date</th> : null}
                    <th className={TH}>Category</th>
                    <th className={TH}>Description</th>
                    {type === 'STL' ? <th className={TH}>Location</th> : null}
                    {type === 'STL' ? <th className={TH}>Receipt no.</th> : null}
                    <th className={`${TH} text-right`}>Amount ({cur})</th>
                    {type === 'ADV' ? <th className={TH}>Notes</th> : null}
                    <th className={TH}>Receipt</th>
                    <th className={TH} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {draft.lines.map((l, i) => (
                    <tr key={i} className="bg-neutral-0">
                      {type === 'STL' ? (
                        <td className={`${TD_INPUT} w-[160px]`}>
                          <input
                            aria-label="Date"
                            type="date"
                            className={INPUT}
                            value={l.date || ''}
                            onChange={(e) => setLine(i, 'date', e.target.value)}
                          />
                        </td>
                      ) : null}
                      <td className={`${TD_INPUT} w-[200px]`}>
                        <RequestSelect
                          ariaLabel="Category"
                          value={l.category || ''}
                          options={CATEGORY_OPTIONS}
                          placeholder="Choose"
                          onChange={(v) => setLine(i, 'category', v)}
                        />
                      </td>
                      <td className={TD_INPUT}>
                        <input
                          aria-label="Description"
                          className={INPUT}
                          value={l.description || ''}
                          onChange={(e) => setLine(i, 'description', e.target.value)}
                        />
                      </td>
                      {type === 'STL' ? (
                        <>
                          <td className={TD_INPUT}>
                            <input
                              aria-label="Location"
                              className={INPUT}
                              value={l.location || ''}
                              onChange={(e) => setLine(i, 'location', e.target.value)}
                            />
                          </td>
                          <td className={`${TD_INPUT} w-[120px]`}>
                            <input
                              aria-label="Receipt no."
                              className={INPUT}
                              value={l.receiptNo || ''}
                              onChange={(e) => setLine(i, 'receiptNo', e.target.value)}
                            />
                          </td>
                        </>
                      ) : null}
                      <td className={`${TD_INPUT} w-[140px]`}>
                        <input
                          aria-label="Amount"
                          type="number"
                          min="0"
                          step="0.01"
                          className={`${INPUT} text-right`}
                          value={l.amount}
                          onChange={(e) => setLine(i, 'amount', e.target.value)}
                        />
                      </td>
                      {type === 'ADV' ? (
                        <td className={TD_INPUT}>
                          <input
                            aria-label="Notes"
                            className={INPUT}
                            value={l.notes || ''}
                            onChange={(e) => setLine(i, 'notes', e.target.value)}
                          />
                        </td>
                      ) : null}
                      <td className={`${TD_INPUT} w-[80px] text-center`}>
                        <input
                          type="checkbox"
                          aria-label="Receipt attached"
                          checked={!!l.receipt}
                          onChange={(e) => setLine(i, 'receipt', e.target.checked)}
                        />
                      </td>
                      <td className={`${TD_INPUT} w-[48px]`}>
                        <button
                          type="button"
                          aria-label="Remove line"
                          className={REMOVE}
                          onClick={() =>
                            set((d) => {
                              d.lines.splice(i, 1);
                              if (!d.lines.length) d.lines.push(blank());
                            })
                          }
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={TFOOT}>
                  <tr>
                    <td colSpan={type === 'ADV' ? 2 : 5} className="px-[16px] py-[12px]">
                      {type === 'ADV' ? 'Total estimated expenses' : 'Total expenses claimed'}
                    </td>
                    <td className="px-[16px] py-[12px] text-right">{money(totals.claimed)}</td>
                    <td colSpan={type === 'ADV' ? 3 : 2} />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-[16px]">
              <button
                type="button"
                className={BTN_OUTLINE}
                onClick={() => set((d) => d.lines.push(blank()))}
              >
                + Add line
              </button>
            </div>
          </section>

          <section className={SECTION}>
            <h2 className={SECTION_TITLE}>
              4. {type === 'ADV' ? 'Advance summary' : 'Settlement summary'}
            </h2>
            {type === 'ADV' ? (
              <div className={FIELDS}>
                <Field label="Total estimated expenses">
                  <div className={READONLY}>{money(totals.claimed, cur)}</div>
                </Field>
                <Field label="Previous advance still unsettled (if any)">
                  <div className={READONLY}>{money(prevOutstanding, cur)}</div>
                </Field>
                <Field label="Amount requested" required>
                  {input('requested', { type: 'number', step: '0.01', min: 0 })}
                </Field>
                <Field label="Check">
                  <div
                    className={`${READONLY} ${
                      totals.check && totals.check !== 'OK'
                        ? 'bg-secondary-50 text-secondary-700'
                        : ''
                    }`}
                  >
                    {totals.check || '—'}
                  </div>
                </Field>
              </div>
            ) : (
              <>
                <dl className="grid max-w-[560px] grid-cols-[1fr_auto] gap-x-[24px] gap-y-[8px] text-[14px]">
                  <dt className="text-neutral-300">Total advance received</dt>
                  <dd className="m-0 text-right">{money(advanced, cur)}</dd>
                  <dt className="text-neutral-300">Total expenses claimed</dt>
                  <dd className="m-0 text-right">{money(totals.claimed, cur)}</dd>
                  <dt className="font-gilroy-medium">Balance</dt>
                  <dd className="m-0 text-right font-gilroy-medium">
                    {money(totals.balance, cur)}
                  </dd>
                  <dt className="text-neutral-300">Settlement outcome</dt>
                  <dd className="m-0 text-right">{totals.outcome}</dd>
                </dl>
                {totals.balance !== 0 ? (
                  <div className={`${FIELDS} mt-[24px]`}>
                    {totals.balance > 0 ? (
                      <>
                        <Field label="If returning cash: date returned">
                          {input('returnDate', { type: 'date' })}
                        </Field>
                        <Field label="Method">{select('returnMethod', RETURN_METHODS)}</Field>
                        <Field label="Return voucher no.">{input('returnVoucher')}</Field>
                      </>
                    ) : (
                      <Field label="Reimbursement method (if owed)">
                        {select('reimburseMethod', REIMBURSE_METHODS)}
                      </Field>
                    )}
                  </div>
                ) : null}
              </>
            )}
          </section>

          <section className={SECTION}>
            <h2 className={SECTION_TITLE}>
              5. {type === 'ADV' ? 'Employee undertaking' : 'Declaration'}
            </h2>
            <label className="flex items-start gap-[12px] rounded-[12px] bg-creams-100 px-[16px] py-[14px] text-[14px] text-neutral-500">
              <input
                type="checkbox"
                className="mt-[3px]"
                checked={draft.acknowledged}
                onChange={(e) => set((d) => (d.acknowledged = e.target.checked))}
              />
              <span>{DECLARATION[type]}</span>
            </label>
          </section>

          <section className={SECTION}>
            <h2 className={SECTION_TITLE}>6. Approvals</h2>
            <p className="m-0 text-[15px] text-neutral-300">
              Line manager{' '}
              <span className="font-gilroy-medium text-neutral-500">
                {manager ? manager.name || manager.userId : '—'}
              </span>{' '}
              <span className="text-neutral-300">·</span> Finance review
            </p>
          </section>
        </div>
      </div>

      <div className="sticky bottom-0 z-30 border-t border-gray-200 bg-neutral-0">
        <div className={`${CONTENT} flex flex-wrap justify-end gap-[6px] py-[12px]`}>
          <button
            type="button"
            className={BTN_SECONDARY}
            disabled={busy}
            onClick={() => save(false)}
          >
            Save draft
          </button>
          {lockReason ? <Tooltip title={lockReason}>{submitButton}</Tooltip> : submitButton}
        </div>
      </div>
    </div>
  );
};
